import React from 'react';
import { Link } from 'react-router';
import type { LscMatch } from '../../types';
import { lscPath } from '../../routing';
import { displaySeasonLabel } from '../../seasonDisplay';
import type { DerivedUpcomingMatches } from './leagueHomeUtils';
import { getScheduleCardLabel } from './leagueHomeUtils';
import TeamMatchupBadge from './TeamMatchupBadge';

export type UpcomingGamesSectionProps = {
  leagueId: string;
  upcoming: DerivedUpcomingMatches;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
};

function formatWhen(value?: string): string {
  if (!value) return 'Schedule TBD';
  try {
    return new Date(value).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function UpcomingMatchCard({
  match,
  label,
}: {
  match: LscMatch;
  label: string;
}) {
  const awayLabel = match.away_side_label?.trim() || 'Away';
  const homeLabel = match.home_side_label?.trim() || 'Home';
  const stageParts = [
    match.stage_label,
    displaySeasonLabel(match.season_label, null, match.edition),
  ].filter(Boolean);

  return (
    <article
      className="shrink-0 w-72 rounded-xl border border-secondary-700 bg-gradient-to-b from-secondary-800/70 to-secondary-900/80 p-4 space-y-3"
      data-testid="upcoming-game-card"
      data-upcoming-label={label}
    >
      <span className="inline-flex rounded-full border border-secondary-600 bg-secondary-900 px-2 py-0.5 text-xs text-secondary-300">
        {label}
      </span>

      <div
        className="flex items-center justify-between gap-3"
        data-testid="upcoming-game-matchup-row"
      >
        <TeamMatchupBadge label={awayLabel} side="away" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-secondary-500">
          VS
        </span>
        <TeamMatchupBadge label={homeLabel} side="home" />
      </div>

      <div className="space-y-1 text-center">
        <p className="text-xs text-secondary-400">
          {stageParts.length > 0 ? stageParts.join(' · ') : 'League schedule'}
        </p>
        <p className="text-xs text-secondary-500">{formatWhen(match.scheduled_at)}</p>
      </div>
    </article>
  );
}

const UpcomingGamesSection: React.FC<UpcomingGamesSectionProps> = ({
  leagueId,
  upcoming,
  loading = false,
  error = null,
  emptyMessage = 'No upcoming games on the schedule yet.',
}) => {
  const cards = [
    ...upcoming.future.map((match) => ({
      match,
      label: getScheduleCardLabel(match, 'future'),
      bucket: 'future' as const,
    })),
    ...upcoming.currentSlate.map((match) => ({
      match,
      label: getScheduleCardLabel(match, 'currentSlate'),
      bucket: 'currentSlate' as const,
    })),
  ].slice(0, 10);

  return (
    <section className="space-y-4" data-testid="upcoming-games-section">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Upcoming Games</h2>
          <p className="text-sm text-secondary-400 mt-1">
            Official schedule — not live status. Watch for streams when games go live.
          </p>
        </div>
        <Link
          to={lscPath(`/leagues/${leagueId}/matches`)}
          className="text-sm text-primary-300 hover:text-primary-200 shrink-0"
        >
          See full schedule
        </Link>
      </div>

      {loading ? (
        <p className="text-sm text-secondary-400">Loading schedule...</p>
      ) : error ? (
        <p className="text-sm text-red-300">{error}</p>
      ) : cards.length === 0 ? (
        <p className="text-sm text-secondary-400">{emptyMessage}</p>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2" data-testid="upcoming-games-row">
          {cards.map(({ match, label, bucket }) => (
            <UpcomingMatchCard
              key={`${bucket}-${match.id}`}
              match={match}
              label={label}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default UpcomingGamesSection;
